import { useCallback } from "react";
import { Release } from "../types";
import { doesUserHaveAccess } from "../utils/doesUserHaveAccess";
import { doesReleaseHaveGoldDiscount } from "../utils/doesReleaseHaveGoldDiscount";
import {
  FunnelConfigTypes,
  FunnelOnSuccessResult,
  FunnelOptionsTypes,
} from "../components/funnel/types";
import { FUNNEL_TYPES } from "../components/funnel/utils/contants";
import { useUserData } from "./useUserData";

interface UseReleasePurchaseFlowProps {
  setFunnelConfig: (config?: FunnelConfigTypes) => void;
  options?: FunnelOptionsTypes;
}

/**
 * Shared hook for handling release purchase flow logic
 * Used by both useReleaseInfoPaywall and useBuyPreviewContext
 */
export function useReleasePurchaseFlow({
  setFunnelConfig,
  options,
}: UseReleasePurchaseFlowProps) {
  const { userData } = useUserData();

  const resolvePurchaseRelease = useCallback(
    (result: FunnelOnSuccessResult) => {
      return (
        result.data?.[1]?.data?.payload?.results ?? result.context?.purchaseRelease
      );
    },
    [],
  );

  const resolveUserHasAccess = useCallback(
    (result: FunnelOnSuccessResult) => {
      const access = doesUserHaveAccess(result.data);
      if (access.userHasAccess) {
        return access;
      }

      const purchaseRelease = resolvePurchaseRelease(result);
      const userHasBuyAccess = !!purchaseRelease?.release_user_purchase;
      return {
        userHasBuyAccess,
        userHasGoldAccess: false,
        userHasAccess: userHasBuyAccess,
      };
    },
    [resolvePurchaseRelease],
  );

  const handleSuccessFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const { lastFunnelType } = result;
      const purchaseRelease = resolvePurchaseRelease(result);
      const { userHasBuyAccess, userHasGoldAccess } =
        resolveUserHasAccess(result);
      const funnelWasAuth =
        lastFunnelType &&
        [
          FUNNEL_TYPES.signup,
          FUNNEL_TYPES.signupEmailPassword,
          FUNNEL_TYPES.login,
        ].includes(lastFunnelType);

      let type;
      if (userHasBuyAccess) {
        if (!funnelWasAuth) {
          setFunnelConfig?.();
          return;
        }
        type = FUNNEL_TYPES.purchaseRelease;
      } else if (userHasGoldAccess) {
        type = funnelWasAuth ? FUNNEL_TYPES.goldAccess : FUNNEL_TYPES.congrats;
      }

      setFunnelConfig?.({
        type,
        context: {
          ...result.context,
          purchaseRelease,
        },
        options: {
          ...result.options,
          onSuccess: () => {
            setFunnelConfig?.();
          },
        },
      });
    },
    [resolvePurchaseRelease, resolveUserHasAccess, setFunnelConfig],
  );

  const handlePostCongratsFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      setFunnelConfig?.({
        type: FUNNEL_TYPES.purchaseRelease,
        context: result.context,
        options: {
          ...result.options,
          onSuccess: handleSuccessFlow,
        },
      });
    },
    [setFunnelConfig, handleSuccessFlow],
  );

  const handlePostPaymentFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      if (result.lastFunnelType === FUNNEL_TYPES.purchaseRelease) {
        handleSuccessFlow(result);
        return;
      }

      // After subscription purchase, the user may already own the release
      // (e.g. from a welcome gift bundle). Show congrats instead of the
      // release purchase step.
      const { userHasAccess } = resolveUserHasAccess(result);
      if (userHasAccess) {
        setFunnelConfig?.({
          type: FUNNEL_TYPES.congrats,
          context: result.context,
          options: {
            ...result.options,
            onSuccess: () => setFunnelConfig?.(),
          },
        });
        return;
      }
      const release = result.context?.purchaseRelease;
      if (
        (result.lastFunnelType === FUNNEL_TYPES.subscription ||
          result.lastFunnelType === FUNNEL_TYPES.offerPaywall) &&
        doesReleaseHaveGoldDiscount(release as Release)
      ) {
        handlePostCongratsFlow(result);
      } else {
        handleSuccessFlow(result);
      }
    },
    [handlePostCongratsFlow, handleSuccessFlow, setFunnelConfig],
  );

  const handlePostAuthFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const { userHasAccess } = resolveUserHasAccess(result);

      if (userHasAccess) {
        handleSuccessFlow(result);
      } else {
        const type = result.nextFunnelType;

        setFunnelConfig?.({
          type,
          context: result.context,
          options: {
            ...result.options,
            onSuccess: handlePostPaymentFlow,
          },
        });
      }
    },
    [handlePostPaymentFlow, handleSuccessFlow, resolveUserHasAccess, setFunnelConfig],
  );

  if (!userData) {
    return (config: FunnelConfigTypes) => {
      if (
        config.type === FUNNEL_TYPES.purchaseRelease ||
        config.type === FUNNEL_TYPES.subscription
      ) {
        setFunnelConfig({
          ...config,
          options: {
            ...config.options,
            ...options,
            skipOnboarding: true,
            onSuccess: (result: FunnelOnSuccessResult) =>
              handlePostAuthFlow({ ...result, nextFunnelType: config.type }),
          },
        });
        return;
      }

      setFunnelConfig({
        type: FUNNEL_TYPES.signup,
        context: config.context,
        options: {
          ...config.options,
          skipOnboarding: true,
          onSuccess: (result: FunnelOnSuccessResult) =>
            handlePostAuthFlow({ ...result, nextFunnelType: config.type }),
        },
      });
    };
  }
  return (config: FunnelConfigTypes) => {
    setFunnelConfig({
      ...config,
      options: {
        ...config.options,
        ...options,
        onSuccess: handlePostPaymentFlow,
      },
    });
  };
}
